import { action, computed, observable } from 'mobx'
import {
  type NumericAnswer,
  type NumericQuestion,
  type NumericFormat,
} from '@/features/jobs/questions/questions'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'
import { Subject } from 'rxjs'

export class NumericQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<NumericQuestion> {
  /** @inheritDoc */
  readonly type = 'Numeric'

  private readonly _flushSubject = new Subject<void>()

  /**
   * Emits when the editor should flush the value.
   */
  readonly flush$ = this._flushSubject.asObservable()

  @observable accessor value: number | null = null
  @observable accessor format: NumericFormat = { type: 'Integer' }

  /** @inheritDoc */
  @computed get isAnswered(): boolean {
    return this.value !== null
  }

  /** @inheritDoc */
  @computed get answer(): NumericAnswer | null {
    if (this.value === null) {
      return null
    }
    return {
      type: 'Numeric',
      value: this.value,
    }
  }

  /** @inheritDoc */
  @computed get textAnswer() {
    if (this.value === null) {
      return null
    }
    const decimals = this.format.type === 'Decimal' ? this.format.places : 0
    return this.value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    })
  }

  /** @inheritDoc */
  @action.bound
  respond(value: number | null) {
    if (this.value === value) {
      return
    }

    if (value !== null && this.format.type === 'Integer') {
      value = Math.round(value)
    }

    this.value = value
    this.notifyAnswerChanged()
  }

  /** @inheritDoc */
  flush() {
    this._flushSubject.next()
  }

  /** @inheritDoc */
  set(question: NumericQuestion) {
    this.format = question.format
    this.value = question.answer ? question.answer.value : null
  }

  /** @inheritDoc */
  validate(): QuestionValidationResult {
    if (!this.answer) {
      return QuestionValidationResult.failed(['Please enter a number'])
    }

    return QuestionValidationResult.ok()
  }
}
